// Single source of truth cho cấu trúc vai trò hội đồng — dùng chung cho
// StudentProjectCouncil (template CRUD) và CouncilForm (gán per-đề tài).
//
// Schema BE (refactor 2026-05) chỉ enforce 2 thứ: `role_key` (chuỗi) +
// `order_num` (int). FE config bên dưới quyết định:
//   - Vai trò nào hiển thị (chairman/secretary/reviewer/commissioner)
//   - Số lượng max của từng vai trò (1 chairman, max 2 reviewer, ...)
//   - Bắt buộc / tùy chọn
//   - Label hiển thị
//
// Muốn thêm vai trò mới (vd "Phó chủ tịch"): chỉ cần thêm 1 entry — BE không
// cần migration, không cần đổi schema.

export const COUNCIL_ROLES = [
  {
    key: "chairman",
    label: "Chủ tịch",
    min: 1,
    max: 1,
    required: true,
    order: 1,
  },
  {
    key: "secretary",
    label: "Thư ký",
    min: 1,
    max: 1,
    required: true,
    order: 2,
  },
  {
    key: "reviewer",
    label: "Phản biện",
    min: 1,
    max: 2,
    required: true,
    order: 3,
  },
  {
    key: "commissioner",
    label: "Ủy viên",
    min: 0,
    max: 7,
    required: false,
    order: 4,
  },
];

// Hàm tạo template members mặc định (1 chairman + 1 secretary + 1 reviewer + 3
// commissioner). Dùng khi tạo template mới ở FE.
export const buildDefaultMembers = () => {
  const out = [];
  COUNCIL_ROLES.forEach((r) => {
    const defaultCount = Math.max(r.min, r.key === "commissioner" ? 3 : r.min);
    for (let i = 1; i <= defaultCount; i++) {
      out.push({
        roleKey: r.key,
        orderNum: i,
        profileId: 0,
        name: "",
        degree: "",
        isPhenikaa: true,
      });
    }
  });
  return out;
};

// Group members theo role_key + sort by order_num. Trả về Map { roleKey: [member,...] }.
export const groupMembersByRole = (members) => {
  const map = {};
  COUNCIL_ROLES.forEach((r) => (map[r.key] = []));
  (members || []).forEach((m) => {
    if (!map[m.roleKey]) map[m.roleKey] = [];
    map[m.roleKey].push(m);
  });
  Object.keys(map).forEach((k) =>
    map[k].sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0))
  );
  return map;
};

// Tìm config role theo key. Trả về null nếu không khớp config — caller xử lý.
export const getRoleConfig = (roleKey) =>
  COUNCIL_ROLES.find((r) => r.key === roleKey) || null;

// Map ordinal cho docx template (ExportForm dùng để gen file Word).
// commissioner/1 → "firstCommissioner", commissioner/2 → "secondCommissioner", ...
const ORDINAL = ["first", "second", "third", "fourth", "fifth", "sixth", "seventh"];

// flattenAssignmentForExport — convert assignment.members[] sang flat object có
// các key tương thích docx template cũ (chairmanName, secretaryName,
// firstReviewerName, firstCommissionerName, ...).
// Dùng cho ExportForm khi xuất Word.
export const flattenAssignmentForExport = (assignment) => {
  const out = {};
  (assignment?.members || []).forEach((m) => {
    let prefix;
    if (m.roleKey === "chairman") prefix = "chairman";
    else if (m.roleKey === "secretary") prefix = "secretary";
    else if (m.roleKey === "reviewer") prefix = (ORDINAL[(m.orderNum || 1) - 1] || "first") + "Reviewer";
    else if (m.roleKey === "commissioner") prefix = (ORDINAL[(m.orderNum || 1) - 1] || "first") + "Commissioner";
    else return;
    out[`${prefix}Name`] = m.name || "";
    out[`${prefix}Degree`] = m.degree || "";
    out[`${prefix}Pid`] = m.profileId || 0;
  });
  return out;
};

// Validate count tối thiểu / tối đa cho mỗi role.
// Trả về null nếu OK, hoặc string lỗi.
export const validateMembers = (members) => {
  const grouped = groupMembersByRole(members);
  for (const r of COUNCIL_ROLES) {
    const count = (grouped[r.key] || []).length;
    if (r.required && count < r.min) {
      return `Thiếu ${r.label} (cần ít nhất ${r.min}, hiện ${count}).`;
    }
    if (count > r.max) {
      return `Quá số lượng ${r.label} (tối đa ${r.max}, hiện ${count}).`;
    }
    // Validate tên không rỗng cho slot required
    for (const m of grouped[r.key] || []) {
      if (r.required && !m.name?.trim()) {
        return `Vui lòng nhập tên cho ${r.label}.`;
      }
    }
  }
  return null;
};
